import React, { type FC, memo } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { List } from 'immutable';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../../../types';
import type { ReduxState } from 'app/redux/types';

import { ContextNamespace } from 'app/constants/Context';
import { withinBettingContext } from 'app/contexts/betting';
import { compareStakeProps, mapBetsToStake } from 'app/lib/betting';
import type { RecordOfBetEntity, RecordOfStakeEntity } from 'app/lib/betting/betting-entities';

import { Bet } from '../../../BetRepresentation';

import betStyles from './SlotBet.module.scss';

// ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyType = any;

type StoreProps = {
  bets: List<RecordOfBetEntity>;
  disabled: boolean;
  fixedBets: Record<number, boolean>;
  conflictedIds: Set<number>;
  addBetAction: (matchId: number, betId: number) => void;
};

type SlotStakeProps = {
  stake: RecordOfStakeEntity;
  dataGaEvent: string;
};


const SlotStake: FC<SlotStakeProps & StoreProps> = memo(({
  stake,
  bets,
  disabled,
  fixedBets,
  conflictedIds,
  addBetAction,
  dataGaEvent,
}) => {
  const dispatch = useDispatch();

  const handleBetClick = (betId: number) => {
    dispatch(addBetAction(stake.matchId, betId));
  };

  return (
    <>
      {bets.sort((a: AnyType, b: AnyType) => a.key - b.key)
        .map((bet) => {
          const isNotConflictedEntry = conflictedIds.size > 0 && !conflictedIds.has(bet.id);
          const dataGaAttributes = {
            ['data-ga-event']: dataGaEvent,
            ['data-ga-event-content']: bet?.id,
            ['data-ga-event-value']: bet?.price,
          };

          return (
            <Bet key={bet.id}
                 bet={bet}
                 isInBetSlip={fixedBets[bet.id]}
                 disabled={disabled || !bet.isOpen}
                 conflict={isNotConflictedEntry}
                 labelType="long"
                 className={betStyles.bet}
                 dataGaAttributes={dataGaAttributes}
                 priceClassName={betStyles.betPrice}
                 onClick={handleBetClick} />
          );
        })}
    </>
  );
}, (prevProps, nextProps) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return !compareStakeProps(prevProps, nextProps);
});

const mapStateToProps = (state: ReduxState, {
  stake,
  $betting: {
    namespace,
    content: {
      selectors: {
        matches,
      },
    },
    matchContent,
    staking,
    matchStaking,
  },
}: BettingContextProps): StoreProps => {
  const isPrematch = namespace === ContextNamespace.PREMATCH;
  const prematchMatches = matchContent?.selectors?.matches;
  const prematchMatchesAddBet = matchStaking?.actions?.addBet;

  const matchesSelector = (isPrematch && prematchMatches) ? prematchMatches : matches;
  const addBetAction = (isPrematch && prematchMatchesAddBet)
    ? prematchMatchesAddBet
    : staking.actions.addBet;

  const mappedProps: AnyType = mapBetsToStake(state, matchesSelector, stake, staking);
  mappedProps.conflictedIds = staking.selectors.selectAllConflictedIds(state);
  mappedProps.addBetAction = addBetAction;

  return mappedProps;
};

export default compose<FC<SlotStakeProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(SlotStake);
